'use client'

import { useSession } from 'next-auth/react'
import { useVariantSelection } from '@/lib/commerce/hooks'
import { getOptions, optionLabel, valueState, visibleSwatches, type ColorSwatch, type OptionType, type OptionValues } from '@/lib/commerce/variantOptions'
import { formatImageURL, formatPrice, getProductDisplayPrice } from '@/utils/util'

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-foreground'

export function OptionPicker({ product, selected, onSelect }: { product: any, selected: OptionValues, onSelect: (type: OptionType, value: string) => void }) {
  return (
    <div className="flex flex-col gap-6">
      {getOptions(product).map(option => (
        <fieldset key={option.type}>
          <legend className="text-[13px]">
            {optionLabel(option)}
            {selected[option.type] && <span className="text-muted-foreground">: {selected[option.type]}</span>}
          </legend>
          <div className="mt-3 flex flex-wrap gap-2">
            {option.values.map(({ value, hex }) => {
              const isSelected = selected[option.type] === value
              const state = valueState(product, selected, option.type, value)
              const unavailable = state !== 'available'
              const label = unavailable ? `${value} — ${state === 'soldOut' ? 'дууссан' : 'байхгүй'}` : value

              if (option.type === 'color') {
                return (
                  <button key={value} type="button" onClick={() => onSelect('color', value)} aria-label={label} aria-pressed={isSelected} title={label} className={`relative size-9 p-1 border transition-colors duration-150 ${isSelected ? 'border-foreground' : 'border-transparent hover:border-border'} ${focusRing}`}>
                    <span className={`block size-full border border-foreground/10 ${unavailable ? 'opacity-40' : ''}`} style={{ backgroundColor: hex }} />
                    {unavailable && (
                      <svg aria-hidden viewBox="0 0 36 36" className="absolute inset-0 size-full text-foreground/70">
                        <line x1="5" y1="31" x2="31" y2="5" stroke="currentColor" strokeWidth="1" />
                      </svg>
                    )}
                  </button>
                )
              }

              const stateClass = isSelected
                ? unavailable ? 'border-foreground text-muted-foreground' : 'border-foreground bg-foreground text-background'
                : unavailable ? 'border-border text-muted-foreground hover:border-foreground' : 'border-border hover:border-foreground'
              return (
                <button key={value} type="button" onClick={() => onSelect(option.type, value)} aria-label={label} aria-pressed={isSelected} className={`inline-flex items-center justify-center min-h-10 min-w-12 px-4 border text-[13px] transition-colors duration-150 ${stateClass} ${focusRing}`}>
                  <span className={unavailable ? 'line-through' : ''}>{value}</span>
                </button>
              )
            })}
          </div>
        </fieldset>
      ))}
    </div>
  )
}

export function CardSwatches({ colors, active, onSelect, max = 6 }: { colors: ColorSwatch[], active: string | null, onSelect: (value: string) => void, max?: number }) {
  if (colors.length === 0) return null
  const shown = visibleSwatches(colors, active, max)

  return (
    <div className="mt-2 -ml-1.25 flex flex-wrap items-center gap-0.5">
      {shown.map(({ value, hex, soldOut }) => {
        const isActive = value === active
        const label = soldOut ? `${value} — дууссан` : value
        return (
          <button key={value} type="button" onClick={() => onSelect(value)} aria-label={label} aria-pressed={isActive} title={label} className={`relative size-6 p-1 border transition-colors duration-150 ${isActive ? 'border-foreground' : 'border-transparent hover:border-border'} ${focusRing}`}>
            <span className={`block size-full border border-foreground/10 ${soldOut ? 'opacity-40' : ''}`} style={{ backgroundColor: hex }} />
            {soldOut && (
              <svg aria-hidden viewBox="0 0 24 24" className="absolute inset-0 size-full text-foreground/70">
                <line x1="4" y1="20" x2="20" y2="4" stroke="currentColor" strokeWidth="1" />
              </svg>
            )}
          </button>
        )
      })}
      {colors.length > shown.length && <span className="ml-1 text-[11px] text-muted-foreground tabular-nums">+{colors.length - shown.length}</span>}
    </div>
  )
}

export function OptionQuickAdd({ product, onAdd }: { product: any, onAdd: (variantId: string) => void }) {
  const { data: session } = useSession()
  const { selectedVariant, selectedOptions, selectOption, isSelectionPurchasable, isVariantAvailable } = useVariantSelection(product)
  const canAdd = isSelectionPurchasable && !!selectedVariant?.id && isVariantAvailable(selectedVariant)
  const price = selectedVariant ? getProductDisplayPrice(product, selectedVariant, !!session?.user?.id) : 0

  return (
    <div>
      <div className="flex items-center gap-4">
        <span className="w-14 aspect-3/4 shrink-0 overflow-hidden bg-muted">
          <img src={formatImageURL(selectedVariant?.images?.[0] || 'placeholder.png')} alt="" className="size-full object-cover" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13px] text-muted-foreground">{isSelectionPurchasable ? selectedVariant?.name : 'Энэ хослол байхгүй'}</div>
          <div className="mt-1 text-sm tabular-nums">{price === 0 ? 'Үнэгүй' : formatPrice(price)}</div>
        </div>
      </div>
      <div className="mt-6">
        <OptionPicker product={product} selected={selectedOptions} onSelect={selectOption} />
      </div>
      <button type="button" disabled={!canAdd} onClick={() => { if (selectedVariant?.id) onAdd(selectedVariant.id) }} className={`mt-8 w-full h-12 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`}>
        {canAdd ? 'Сагсанд хийх' : isSelectionPurchasable ? 'Дууссан байна' : 'Энэ хослол байхгүй'}
      </button>
    </div>
  )
}
